import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  TagIcon,
  DocumentTextIcon,
  BookOpenIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  CogIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  ArchiveBoxIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import type { CategoriaUI as Categoria } from './tipos'

interface ModalCategoriaProps {
  isOpen: boolean
  onClose: () => void
  tipo: 'ver' | 'crear' | 'editar' | 'eliminar'
  categoria?: Categoria | null
  categoriasPadre?: Categoria[]
  onGuardar: (categoria: Partial<Categoria>) => Promise<void> | void
  onEliminar: (id: string) => Promise<void> | void
}

const ModalCategoria: React.FC<ModalCategoriaProps> = ({
  isOpen,
  onClose,
  tipo,
  categoria,
  categoriasPadre = [],
  onGuardar,
  onEliminar
}) => {
  const [formData, setFormData] = useState<Partial<Categoria>>({
    nombre: '',
    descripcion: '',
    color: '#3B82F6',
    icono: 'folder',
    padre: '',
    estado: 'activa',
    metadatos: {
      keywords: [],
      tipo: 'educativo',
      acceso: 'publico'
    }
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [cargando, setCargando] = useState(false)
  const [keywordInput, setKeywordInput] = useState('')
  const [mostrarColores, setMostrarColores] = useState(false)
  const [mostrarIconos, setMostrarIconos] = useState(false)

  const coloresDisponibles = [
    { nombre: 'Azul', valor: '#3B82F6' },
    { nombre: 'Verde', valor: '#10B981' },
    { nombre: 'Púrpura', valor: '#8B5CF6' },
    { nombre: 'Amarillo', valor: '#F59E0B' },
    { nombre: 'Rojo', valor: '#EF4444' },
    { nombre: 'Rosa', valor: '#EC4899' },
    { nombre: 'Índigo', valor: '#6366F1' },
    { nombre: 'Gris', valor: '#6B7280' },
    { nombre: 'Naranja', valor: '#F97316' },
    { nombre: 'Teal', valor: '#14B8A6' },
    { nombre: 'Cyan', valor: '#06B6D4' },
    { nombre: 'Lima', valor: '#84CC16' }
  ]

  const iconosDisponibles = [
    { nombre: 'Carpeta', valor: 'folder', icono: <FolderIcon className="h-5 w-5" /> },
    { nombre: 'Etiqueta', valor: 'tag', icono: <TagIcon className="h-5 w-5" /> },
    { nombre: 'Documento', valor: 'document', icono: <DocumentTextIcon className="h-5 w-5" /> },
    { nombre: 'Libro', valor: 'book', icono: <BookOpenIcon className="h-5 w-5" /> },
    { nombre: 'Académico', valor: 'academic', icono: <AcademicCapIcon className="h-5 w-5" /> },
    { nombre: 'Maletín', valor: 'briefcase', icono: <BriefcaseIcon className="h-5 w-5" /> },
    { nombre: 'Configuración', valor: 'cog', icono: <CogIcon className="h-5 w-5" /> },
    { nombre: 'Imagen', valor: 'photo', icono: <PhotoIcon className="h-5 w-5" /> },
    { nombre: 'Video', valor: 'video', icono: <VideoCameraIcon className="h-5 w-5" /> },
    { nombre: 'Audio', valor: 'audio', icono: <MusicalNoteIcon className="h-5 w-5" /> },
    { nombre: 'Archivo', valor: 'archive', icono: <ArchiveBoxIcon className="h-5 w-5" /> }
  ]

  useEffect(() => {
    if (categoria && (tipo === 'ver' || tipo === 'editar')) {
      setFormData({
        ...categoria,
        metadatos: categoria.metadatos || {
          keywords: [],
          tipo: 'educativo',
          acceso: 'publico'
        }
      })
    } else if (tipo === 'crear') {
      setFormData({
        nombre: '',
        descripcion: '',
        color: '#3B82F6',
        icono: 'folder',
        padre: '',
        estado: 'activa',
        metadatos: {
          keywords: [],
          tipo: 'educativo',
          acceso: 'publico'
        }
      })
    }
    setErrors({})
  }, [categoria, tipo, isOpen])

  const validarFormulario = (): boolean => {
    const nuevosErrores: Record<string, string> = {}

    if (!formData.nombre?.trim()) {
      nuevosErrores.nombre = 'El nombre es obligatorio'
    } else if (formData.nombre.length < 2) {
      nuevosErrores.nombre = 'El nombre debe tener al menos 2 caracteres'
    } else if (formData.nombre.length > 50) {
      nuevosErrores.nombre = 'El nombre no puede exceder 50 caracteres'
    }

    if (formData.descripcion && formData.descripcion.length > 200) {
      nuevosErrores.descripcion = 'La descripción no puede exceder 200 caracteres'
    }

    if (!formData.color) {
      nuevosErrores.color = 'Debe seleccionar un color'
    }

    if (!formData.icono) {
      nuevosErrores.icono = 'Debe seleccionar un icono'
    }

    setErrors(nuevosErrores)
    return Object.keys(nuevosErrores).length === 0
  }

  const manejarSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validarFormulario()) {
      return
    }

    setCargando(true)
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simular API call
      
      const datosParaGuardar = {
        ...formData,
        fechaModificacion: tipo === 'editar' ? new Date().toISOString() : undefined,
        fechaCreacion: tipo === 'crear' ? new Date().toISOString() : formData.fechaCreacion,
        creadoPor: tipo === 'crear' ? 'Usuario Actual' : formData.creadoPor,
        totalArchivos: tipo === 'crear' ? 0 : formData.totalArchivos,
        subcategorias: tipo === 'crear' ? [] : formData.subcategorias || [],
        nivel: formData.padre ? 1 : 0,
        orden: tipo === 'crear' ? Date.now() : formData.orden
      }
      
      onGuardar(datosParaGuardar)
      onClose()
    } catch (error) {
      console.error('Error al guardar categoría:', error)
    } finally {
      setCargando(false)
    }
  }

  const manejarEliminar = async () => {
    if (!categoria?.id) return
    
    setCargando(true)
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simular API call
      onEliminar(categoria.id)
      onClose()
    } catch (error) {
      console.error('Error al eliminar categoría:', error)
    } finally {
      setCargando(false)
    }
  }

  const agregarKeyword = () => {
    if (keywordInput.trim() && formData.metadatos) {
      const keywords = [...(formData.metadatos.keywords || []), keywordInput.trim()]
      setFormData(prev => ({
        ...prev,
        metadatos: {
          ...prev.metadatos!,
          keywords
        }
      }))
      setKeywordInput('')
    }
  }

  const eliminarKeyword = (index: number) => {
    if (formData.metadatos) {
      const keywords = (formData.metadatos.keywords ?? []).filter((_, i) => i !== index)
      setFormData(prev => ({
        ...prev,
        metadatos: {
          ...prev.metadatos!,
          keywords
        }
      }))
    }
  }

  const obtenerIconoSeleccionado = () => {
    const iconoEncontrado = iconosDisponibles.find(icono => icono.valor === formData.icono)
    return iconoEncontrado?.icono || <FolderIcon className="h-5 w-5" />
  }

  if (!isOpen) return null
  const formIdPrefix = 'modal-categoria'

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              {tipo === 'ver' && <EyeIcon className="h-6 w-6 text-blue-600" />}
              {tipo === 'crear' && <FolderIcon className="h-6 w-6 text-green-600" />}
              {tipo === 'editar' && <TagIcon className="h-6 w-6 text-yellow-600" />}
              {tipo === 'eliminar' && <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {tipo === 'ver' && 'Ver Categoría'}
                {tipo === 'crear' && 'Crear Nueva Categoría'}
                {tipo === 'editar' && 'Editar Categoría'}
                {tipo === 'eliminar' && 'Eliminar Categoría'}
              </h3>
            </div>
            <button
              onClick={onClose}
              aria-label="Cerrar modal de categoría"
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {tipo === 'eliminar' ? (
              <div className="text-center">
                <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  ¿Estás seguro?
                </h4>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Esta acción eliminará permanentemente la categoría "{categoria?.nombre}" y todas sus subcategorías.
                  {categoria?.totalArchivos && categoria.totalArchivos > 0 && (
                    <span className="block mt-2 text-red-600 dark:text-red-400 font-medium">
                      ⚠️ Esta categoría contiene {categoria.totalArchivos} archivos que se moverán a "Sin categoría"
                    </span>
                  )}
                </p>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={onClose}
                    disabled={cargando}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={manejarEliminar}
                    disabled={cargando}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2"
                  >
                    {cargando && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                    <span>Eliminar</span>
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={manejarSubmit} className="space-y-6">
                {/* Información básica */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor={`${formIdPrefix}-nombre`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nombre *
                    </label>
                    <input
                      id={`${formIdPrefix}-nombre`}
                      name="nombre"
                      type="text"
                      value={formData.nombre || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                      disabled={tipo === 'ver'}
                      aria-invalid={Boolean(errors.nombre)}
                      aria-describedby={errors.nombre ? `${formIdPrefix}-nombre-error` : undefined}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                        errors.nombre ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      } ${tipo === 'ver' ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
                      placeholder="Nombre de la categoría"
                    />
                    {errors.nombre && (
                      <p id={`${formIdPrefix}-nombre-error`} role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.nombre}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor={`${formIdPrefix}-estado`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Estado
                    </label>
                    <select
                      id={`${formIdPrefix}-estado`}
                      name="estado"
                      value={formData.estado || 'activa'}
                      onChange={(e) => setFormData(prev => ({ ...prev, estado: e.target.value as any }))}
                      disabled={tipo === 'ver'}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                        tipo === 'ver' ? 'bg-gray-50 dark:bg-gray-600' : ''
                      }`}
                    >
                      <option value="activa">Activa</option>
                      <option value="inactiva">Inactiva</option>
                      <option value="archivada">Archivada</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor={`${formIdPrefix}-descripcion`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Descripción
                  </label>
                  <textarea
                    id={`${formIdPrefix}-descripcion`}
                    name="descripcion"
                    value={formData.descripcion || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                    disabled={tipo === 'ver'}
                    rows={3}
                    aria-invalid={Boolean(errors.descripcion)}
                    aria-describedby={errors.descripcion ? `${formIdPrefix}-descripcion-error` : undefined}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                      errors.descripcion ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } ${tipo === 'ver' ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
                    placeholder="Descripción de la categoría"
                  />
                  {errors.descripcion && (
                    <p id={`${formIdPrefix}-descripcion-error`} role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.descripcion}</p>
                  )}
                </div>

                {/* Categoría padre */}
                {categoriasPadre.length > 0 && (
                  <div>
                    <label htmlFor={`${formIdPrefix}-padre`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Categoría Padre
                    </label>
                    <select
                      id={`${formIdPrefix}-padre`}
                      name="padre"
                      value={formData.padre || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, padre: e.target.value || undefined }))}
                      disabled={tipo === 'ver'}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                        tipo === 'ver' ? 'bg-gray-50 dark:bg-gray-600' : ''
                      }`}
                    >
                      <option value="">Sin categoría padre (Principal)</option>
                      {categoriasPadre.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Apariencia */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor={`${formIdPrefix}-color`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Color
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        id={`${formIdPrefix}-color`}
                        name="color"
                        onClick={() => tipo !== 'ver' && setMostrarColores(!mostrarColores)}
                        disabled={tipo === 'ver'}
                        aria-haspopup="dialog"
                        aria-expanded={mostrarColores}
                        aria-label="Seleccionar color de la categoría"
                        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 flex items-center space-x-2 ${
                          tipo === 'ver' ? 'bg-gray-50 dark:bg-gray-600' : 'hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        <div
                          className="w-6 h-6 rounded-full border border-gray-300"
                          style={{ backgroundColor: formData.color }}
                        ></div>
                        <span className="text-gray-900 dark:text-white">{formData.color}</span>
                      </button>
                      
                      {mostrarColores && tipo !== 'ver' && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10 p-3">
                          <div className="grid grid-cols-4 gap-2">
                            {coloresDisponibles.map(color => (
                              <button
                                key={color.valor}
                                type="button"
                                aria-label={`Seleccionar color ${color.nombre}`}
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, color: color.valor }))
                                  setMostrarColores(false)
                                }}
                                className="w-8 h-8 rounded-full border-2 border-gray-300 hover:border-gray-500 transition-colors"
                                style={{ backgroundColor: color.valor }}
                                title={color.nombre}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor={`${formIdPrefix}-icono`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Icono
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        id={`${formIdPrefix}-icono`}
                        name="icono"
                        onClick={() => tipo !== 'ver' && setMostrarIconos(!mostrarIconos)}
                        disabled={tipo === 'ver'}
                        aria-haspopup="dialog"
                        aria-expanded={mostrarIconos}
                        aria-label="Seleccionar icono de la categoría"
                        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 flex items-center space-x-2 ${
                          tipo === 'ver' ? 'bg-gray-50 dark:bg-gray-600' : 'hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        <div className="text-gray-600 dark:text-gray-300">
                          {obtenerIconoSeleccionado()}
                        </div>
                        <span className="text-gray-900 dark:text-white">
                          {iconosDisponibles.find(i => i.valor === formData.icono)?.nombre || 'Carpeta'}
                        </span>
                      </button>
                      
                      {mostrarIconos && tipo !== 'ver' && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10 p-3 max-h-48 overflow-y-auto">
                          <div className="grid grid-cols-2 gap-2">
                            {iconosDisponibles.map(icono => (
                              <button
                                key={icono.valor}
                                type="button"
                                aria-label={`Seleccionar icono ${icono.nombre}`}
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, icono: icono.valor }))
                                  setMostrarIconos(false)
                                }}
                                className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-left"
                              >
                                <div className="text-gray-600 dark:text-gray-300">
                                  {icono.icono}
                                </div>
                                <span className="text-sm text-gray-900 dark:text-white">
                                  {icono.nombre}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Metadatos */}
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">Metadatos</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor={`${formIdPrefix}-tipo`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tipo
                      </label>
                      <select
                        id={`${formIdPrefix}-tipo`}
                        name="metadatos.tipo"
                        value={formData.metadatos?.tipo || 'educativo'}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          metadatos: {
                            ...prev.metadatos!,
                            tipo: e.target.value as any
                          }
                        }))}
                        disabled={tipo === 'ver'}
                        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          tipo === 'ver' ? 'bg-gray-50 dark:bg-gray-600' : ''
                        }`}
                      >
                        <option value="educativo">Educativo</option>
                        <option value="administrativo">Administrativo</option>
                        <option value="operativo">Operativo</option>
                        <option value="comercial">Comercial</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor={`${formIdPrefix}-acceso`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Nivel de Acceso
                      </label>
                      <select
                        id={`${formIdPrefix}-acceso`}
                        name="metadatos.acceso"
                        value={formData.metadatos?.acceso || 'publico'}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          metadatos: {
                            ...prev.metadatos!,
                            acceso: e.target.value as any
                          }
                        }))}
                        disabled={tipo === 'ver'}
                        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          tipo === 'ver' ? 'bg-gray-50 dark:bg-gray-600' : ''
                        }`}
                      >
                        <option value="publico">Público</option>
                        <option value="privado">Privado</option>
                        <option value="restringido">Restringido</option>
                      </select>
                    </div>
                  </div>

                  {/* Keywords */}
                  <div>
                    <label htmlFor={`${formIdPrefix}-keyword`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Palabras Clave
                    </label>
                    {tipo !== 'ver' && (
                      <div className="flex space-x-2 mb-3">
                        <input
                          id={`${formIdPrefix}-keyword`}
                          name="keywordInput"
                          type="text"
                          value={keywordInput}
                          onChange={(e) => setKeywordInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), agregarKeyword())}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          placeholder="Agregar palabra clave"
                        />
                        <button
                          type="button"
                          onClick={agregarKeyword}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                        >
                          Agregar
                        </button>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {(formData.metadatos?.keywords ?? []).map((keyword, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                        >
                          {keyword}
                          {tipo !== 'ver' && (
                            <button
                              type="button"
                              aria-label={`Eliminar palabra clave ${keyword}`}
                              onClick={() => eliminarKeyword(index)}
                              className="ml-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Información adicional para vista */}
                {tipo === 'ver' && categoria && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
                    <h4 className="font-medium text-gray-900 dark:text-white">Información Adicional</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Archivos:</span>
                        <span className="ml-2 font-medium text-gray-900 dark:text-white">{categoria.totalArchivos}</span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Subcategorías:</span>
                        <span className="ml-2 font-medium text-gray-900 dark:text-white">{categoria.subcategorias.length}</span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Creado:</span>
                        <span className="ml-2 font-medium text-gray-900 dark:text-white">
                          {new Date(categoria.fechaCreacion).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Por:</span>
                        <span className="ml-2 font-medium text-gray-900 dark:text-white">{categoria.creadoPor}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Botones */}
                {tipo !== 'ver' && (
                  <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={cargando}
                      className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={cargando}
                      className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2"
                    >
                      {cargando && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                      <span>{tipo === 'crear' ? 'Crear' : 'Guardar'}</span>
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default ModalCategoria
